import React, { Ref } from 'react';

// material-ui
import { useTheme } from '@mui/material/styles';
import {
  Card,
  CardContent,
  CardHeader,
  Divider,
  Typography,
  CardProps,
  CardHeaderProps,
  CardContentProps,
  LinearProgress,
} from '@mui/material';

// constant
const defaultHeaderSX = {
  '& .MuiCardHeader-action': { mr: 0 },
};

// ==============================|| CUSTOM MAIN CARD ||============================== //

export interface MainCardProps {
  border?: boolean;
  boxShadow?: boolean;
  children: React.ReactNode | string;
  style?: React.CSSProperties;
  content?: boolean;
  className?: string;
  contentClass?: string;
  contentSX?: CardContentProps['sx'];
  headerSX?: CardHeaderProps['sx'];
  darkTitle?: boolean;
  sx?: CardProps['sx'];
  secondary?: CardHeaderProps['action'];
  shadow?: string;
  elevation?: number;
  title?: React.ReactNode | string;
  highlight?: boolean;
  loading?: boolean;
  key?: string;
  showDivider?: boolean;
  variant?: CardProps['variant'];
  bottomContent?: React.ReactNode | null;
  bannerContent?: React.ReactNode | null;
}

const MainCard = React.forwardRef(
  (
    {
      border = true,
      boxShadow,
      children,
      content = true,
      contentClass = '',
      contentSX = {},
      headerSX = {},
      darkTitle,
      secondary,
      shadow,
      sx = {},
      title,
      highlight = false,
      loading = false,
      key,
      showDivider = true,
      bottomContent,
      bannerContent,
      ...others
    }: MainCardProps,
    ref: Ref<HTMLDivElement>
  ) => {
    const theme = useTheme();

    return (
      <Card
        ref={ref}
        {...others}
        key={key}
        sx={{
          border: border ? '1px solid' : 'none',
          borderColor: highlight
            ? theme.palette.success.main
            : theme.palette.grey[300],
          ':hover': {
            boxShadow: boxShadow
              ? shadow ||
                (theme.palette.mode === 'dark'
                  ? '0 2px 14px 0 rgb(33 150 243 / 10%)'
                  : '0 2px 14px 0 rgb(32 40 45 / 8%)')
              : 'inherit',
          },
          ...sx,
        }}
      >
        {!!bannerContent && bannerContent}
        {loading && <LinearProgress color="primary" />}
        {/* card header and action */}
        {title && (
          <CardHeader
            sx={{ ...headerSX, ...defaultHeaderSX }}
            title={
              darkTitle ? <Typography variant="h3">{title}</Typography> : title
            }
            action={secondary}
          />
        )}

        {/* content & header divider */}
        {title && showDivider && <Divider />}

        {/* card content */}
        {content && (
          <CardContent sx={contentSX} className={contentClass}>
            {children}
          </CardContent>
        )}
        {!content && children}
        {!!bottomContent && bottomContent}
      </Card>
    );
  }
);

export default MainCard;
